import React from 'react';
import { emitCustomEvent } from 'react-custom-events';

import { UxContext } from '@client/context/UxContext';
import usePageConfig from '@client/hooks/usePageConfig';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { LinkedZoneDoc } from '@ledium/types';
import { LinkTypeChip, RespButton, TitleAndDescription, TreeDnd, TreeDndDropPosition } from '@ledium/ui-common';
import { InlineLinksSection, ManageLinksDialog, ZoneWizard } from '@ledium/ui-project-editor';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import { Alert, Box, Button, ButtonGroup, Chip, Container, FormControlLabel, Grid, IconButton, Popover, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { AddEditZoneDialog, AddZoneFormData } from './AddEditZoneDialog';

const GridButtonContainer = styled(Grid)(({ theme }) => ({
    position: 'sticky',
    top: '64px',
    background: theme.palette.background.default,
    zIndex: 1000,
    marginBottom: '10px'
}));

interface ZoneItem {
    id: string;
    name: string;
    description: string;
    parent: string;
    order?: number;
    virtual?: true;
    virtualSource?: { zoneId: string; type: string };
    linkCount?: number;
}

interface ItemWithZoneId {
    id: string;
    name: string;
    zoneId: string | null;
    running?: boolean;
}

const chipSx = { height: 20, fontSize: '0.65rem' };

function virtualZoneStatusChip(disabledReason: string | null | undefined) {
    if (!disabledReason) {
        return <Chip size="small" color="success" label={i18n('linked_project_status_enabled')} sx={chipSx} />;
    }
    if (disabledReason === 'mapping_error') {
        return <Chip size="small" color="warning" label={i18n('linked_project_status_mapping_error')} sx={chipSx} />;
    }
    return <Chip size="small" color="error" variant="outlined" label={i18n('linked_project_status_disabled')} sx={chipSx} />;
}

interface HoverListItemProps {
    label: string;
    names: string[];
}

function HoverListItem({ label, names }: HoverListItemProps) {
    const [anchor, setAnchor] = React.useState<Element | null>(null);
    return (
        <>
            <Box component="li" sx={{ cursor: 'default' }} onMouseEnter={(e) => setAnchor(e.currentTarget)} onMouseLeave={() => setAnchor(null)}>
                <Typography variant="body2">{label}</Typography>
            </Box>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
            >
                <Box sx={{ p: 1.5 }}>
                    {names.map((name) => (
                        <Typography key={name} variant="body2">
                            {name}
                        </Typography>
                    ))}
                </Box>
            </Popover>
        </>
    );
}

function buildForceDeleteMessage(
    zoneName: string,
    linkedZones: ZoneItem[],
    projects: ItemWithZoneId[],
    mixes: ItemWithZoneId[],
    channels: ItemWithZoneId[]
): React.ReactNode {
    const runningProjects = projects.filter((p) => p.running);

    return (
        <>
            <Alert severity="error" sx={{ mb: 1 }}>
                Warning — this action cannot be undone.
            </Alert>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
                It will delete Zone <strong>{zoneName}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
                The following associated data will be permanently deleted:
            </Typography>
            <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                {linkedZones.length > 0 && (
                    <HoverListItem label={`${linkedZones.length} linked zone${linkedZones.length === 1 ? '' : 's'}`} names={linkedZones.map((z) => z.name)} />
                )}
                {projects.length > 0 && (
                    <HoverListItem label={`${projects.length} project${projects.length === 1 ? '' : 's'}`} names={projects.map((p) => p.name)} />
                )}
                {mixes.length > 0 && <HoverListItem label={`${mixes.length} spectrum mix${mixes.length === 1 ? '' : 'es'}`} names={mixes.map((m) => m.name)} />}
                {channels.length > 0 && (
                    <HoverListItem label={`${channels.length} lighting device${channels.length === 1 ? '' : 's'}`} names={channels.map((c) => c.name)} />
                )}
            </Box>
            {runningProjects.length > 0 && (
                <Alert severity="warning">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        The following projects are currently running and will stop immediately:
                    </Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
                        {runningProjects.map((p) => (
                            <Box key={p.id} component="li">
                                <Typography variant="body2">{p.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Alert>
            )}
        </>
    );
}

interface LinkedZonesPopoverProps {
    zone: ZoneItem;
    linksRefreshKey: number;
    anchorEl: Element | null;
    open: boolean;
    onClose: () => void;
}

function LinkedZonesPopover({ zone, linksRefreshKey, anchorEl, open, onClose }: LinkedZonesPopoverProps) {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{ pointerEvents: 'none' }}
        >
            <Box sx={{ p: 2, minWidth: 240 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {i18n('linked_zone_inline_header', zone.linkCount)}
                </Typography>
                {open && <InlineLinksSection zoneId={zone.id} linkCount={zone.linkCount!} refreshKey={linksRefreshKey} hideHeader />}
            </Box>
        </Popover>
    );
}

interface LinkedZoneTargetIconPrefixProps {
    disabledReason: 'user' | 'mapping_error' | null | undefined;
    sourceZone: ZoneItem | undefined;
    linkType: string;
}

function LinkedZoneTargetIconPrefix({ disabledReason, sourceZone, linkType }: LinkedZoneTargetIconPrefixProps) {
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const open = Boolean(anchorEl);
    let iconColor = 'success.main';
    if (disabledReason === 'mapping_error') {
        iconColor = 'error.main';
    } else if (disabledReason === 'user') {
        iconColor = 'text.disabled';
    }

    return (
        <>
            <LinkIcon
                fontSize="small"
                sx={{ color: iconColor, mr: 0.5, flexShrink: 0 }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
            />
            <Box sx={{ mr: 1 }}>
                <LinkTypeChip type={linkType} />
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
            >
                <Box sx={{ p: 2, minWidth: 240 }}>
                    {sourceZone ? (
                        <Typography variant="body2">
                            Linked zone — source: <strong>{sourceZone.name}</strong>
                        </Typography>
                    ) : (
                        <Typography variant="body2">Linked zone</Typography>
                    )}
                </Box>
            </Popover>
        </>
    );
}

interface LinkedZonesIconPrefixProps {
    zone: ZoneItem;
    linksRefreshKey: number;
    iconColor: 'text.disabled' | 'success.main' | 'error.main';
}

function LinkedZonesIconPrefix({ zone, linksRefreshKey, iconColor }: LinkedZonesIconPrefixProps) {
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <DeviceHubIcon
                fontSize="small"
                sx={{ color: iconColor, mr: 1, flexShrink: 0 }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
            />
            <LinkedZonesPopover zone={zone} linksRefreshKey={linksRefreshKey} anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} />
        </>
    );
}

interface LinkedZonesSuffixProps {
    zone: ZoneItem;
    linksRefreshKey: number;
    onManageLinks: () => void;
}

function LinkedZonesSuffix({ zone, linksRefreshKey, onManageLinks }: LinkedZonesSuffixProps) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ cursor: 'pointer' }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {i18n('linked_zone_inline_header', zone.linkCount)}
            </Typography>
            <LinkedZonesPopover zone={zone} linksRefreshKey={linksRefreshKey} anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} />
            <Button
                size="small"
                variant="outlined"
                sx={{ p: 0 }}
                onClick={(e) => {
                    e.stopPropagation();
                    onManageLinks();
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {i18n('details')}
            </Button>
        </Box>
    );
}

const ZonesMainMenu = () => {
    usePageConfig('zones');
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;

    const [zones, setZones] = React.useState<ZoneItem[]>([]);
    const [zonesWithDevices, setZonesWithDevices] = React.useState<Set<string>>(new Set());
    const [inUseZoneIds, setInUseZoneIds] = React.useState<Set<string>>(new Set());
    const [zonesWithChildren, setZonesWithChildren] = React.useState<Set<string>>(new Set());
    const [loading, setLoading] = React.useState(false);

    const [linkStatusByTargetId, setLinkStatusByTargetId] = React.useState<Map<string, LinkedZoneDoc['disabledReason']>>(new Map());
    const [linkIdByTargetId, setLinkIdByTargetId] = React.useState<Map<string, string>>(new Map());
    const [linkSourceByTargetId, setLinkSourceByTargetId] = React.useState<Map<string, { sourceZoneId: string; enabled: boolean }>>(new Map());
    const [hoveredId, setHoveredId] = React.useState<string | null>(null);
    const [hoveredSourceId, setHoveredSourceId] = React.useState<string | null>(null);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [editZone, setEditZone] = React.useState<ZoneItem | null>(null);
    const [editOpen, setEditOpen] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [managingLinksZone, setManagingLinksZone] = React.useState<ZoneItem | null>(null);
    const [linksRefreshKey, setLinksRefreshKey] = React.useState(0);
    const [wizardState, setWizardState] = React.useState<{ zoneId: string; scenario: 'copy' | 'link-sync' } | null>(null);
    const [forceDeleteEnabled, setForceDeleteEnabled] = React.useState(false);
    const [forceDeleteInfoAnchor, setForceDeleteInfoAnchor] = React.useState<Element | null>(null);
    const [channelItems, setChannelItems] = React.useState<ItemWithZoneId[]>([]);
    const [mixItems, setMixItems] = React.useState<ItemWithZoneId[]>([]);
    const [projectItems, setProjectItems] = React.useState<ItemWithZoneId[]>([]);

    async function loadData() {
        setLoading(true);
        try {
            const [groupsData, channelsData, mixesData, projectsData] = await Promise.all([
                lediumApi.get<ZoneItem[]>('zones'),
                lediumApi.get<ItemWithZoneId[]>('lightChannels'),
                lediumApi.get<ItemWithZoneId[]>('spectrumMixes'),
                lediumApi.get<ItemWithZoneId[]>('projects')
            ]);

            const fetchedZones = (groupsData ?? []).filter((z) => z.id !== 'root');
            setZones(fetchedZones);

            const sourceZoneIds = fetchedZones
                .filter((z) => (z.linkCount ?? 0) > 0)
                .map((z) => z.id)
                .join(',');
            if (sourceZoneIds) {
                const linksData = await lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${sourceZoneIds}`);
                setLinkStatusByTargetId(new Map((linksData ?? []).map((l) => [l.targetZoneId, l.disabledReason])));
                setLinkIdByTargetId(new Map((linksData ?? []).map((l) => [l.targetZoneId, l._id])));
                setLinkSourceByTargetId(new Map((linksData ?? []).map((l) => [l.targetZoneId, { sourceZoneId: l.sourceZoneId, enabled: l.enabled }])));
            } else {
                setLinkStatusByTargetId(new Map());
                setLinkIdByTargetId(new Map());
                setLinkSourceByTargetId(new Map());
            }

            const deviceGroups = new Set<string>((channelsData ?? []).map((c) => c.zoneId).filter((id): id is string => id !== null));
            setZonesWithDevices(deviceGroups);

            const inUse = new Set<string>();
            [...(channelsData ?? []), ...(mixesData ?? []), ...(projectsData ?? [])].forEach((itm) => {
                if (itm.zoneId) {
                    inUse.add(itm.zoneId);
                }
            });

            const withChildren = new Set<string>();
            fetchedZones.forEach((z) => {
                if (z.parent && z.parent !== 'root') {
                    inUse.add(z.parent);
                    withChildren.add(z.parent);
                }
            });

            setZonesWithChildren(withChildren);
            setInUseZoneIds(inUse);
            setChannelItems(channelsData ?? []);
            setMixItems(mixesData ?? []);
            setProjectItems(projectsData ?? []);
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        loadData();
    }, []);

    function notifyChanged() {
        emitCustomEvent('zonesChanged', {});
    }

    async function handleDelete(zone: ZoneItem, force = false) {
        if (zone.virtual) {
            const linkId = linkIdByTargetId.get(zone.id);
            if (linkId) {
                const result = await lediumApi.delete(`linkedZones/${linkId}`);
                if (result) {
                    handleSnackbar({ message: i18n('X_is_successfully_deleted', zone.name) as string });
                } else {
                    handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
                }
            }
        } else if (force) {
            const result = await lediumApi.delete(`zones/${zone.id}/force`);
            if (result) {
                handleSnackbar({ message: i18n('X_is_successfully_deleted', zone.name) as string });
            } else {
                handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
            }
        } else {
            const result = await lediumApi.delete(`zones/${zone.id}`);
            if (result) {
                handleSnackbar({ message: i18n('X_is_successfully_deleted', zone.name) as string });
            } else {
                handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
            }
        }
        await loadData();
        notifyChanged();
    }

    async function handleDeleteClick(zone: ZoneItem) {
        if (forceDeleteEnabled && !zone.virtual) {
            const linkedZonesForZone = zones.filter((z) => z.virtualSource?.zoneId === zone.id);
            const projectsForZone = projectItems.filter((p) => p.zoneId === zone.id);
            const mixesForZone = mixItems.filter((m) => m.zoneId === zone.id);
            const channelsForZone = channelItems.filter((c) => c.zoneId === zone.id);
            const hasItems = linkedZonesForZone.length > 0 || projectsForZone.length > 0 || mixesForZone.length > 0 || channelsForZone.length > 0;

            if (hasItems) {
                handleAlert({
                    title: i18n('are_you_sure') as string,
                    okText: 'Delete',
                    message: buildForceDeleteMessage(zone.name, linkedZonesForZone, projectsForZone, mixesForZone, channelsForZone),
                    destructive: true,
                    okCallback: () => handleDelete(zone, true)
                });
            } else {
                handleAlert({
                    title: i18n('are_you_sure') as string,
                    message: i18n('do_you_want_to_delete_X', zone.name) as string,
                    destructive: true,
                    okCallback: () => handleDelete(zone)
                });
            }
            return;
        }

        if (zone.virtual) {
            const linkInfo = linkSourceByTargetId.get(zone.id);
            if (linkInfo?.enabled) {
                const running = await lediumApi.get<{ name: string | null }>(`zones/${linkInfo.sourceZoneId}/runningProject`);
                if (running?.name) {
                    handleAlert({
                        title: i18n('linked_zone_delete_running_title') as string,
                        message: i18n('linked_zone_delete_running_message', running.name) as string,
                        destructive: true,
                        okCallback: () => handleDelete(zone)
                    });
                    return;
                }
            }
        }
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: i18n('do_you_want_to_delete_X', zone.name) as string,
            destructive: true,
            okCallback: () => handleDelete(zone)
        });
    }

    async function handleEditSave(data: AddZoneFormData) {
        if (!editZone) {
            return;
        }
        const result = await lediumApi.patch(`zones/${editZone.id}`, data);
        if (result) {
            handleSnackbar({ message: i18n('zone_updated') as string });
        } else {
            handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
        }
        await loadData();
        notifyChanged();
    }

    async function handleAddSave(data: AddZoneFormData) {
        const siblingCount = zones.filter((z) => (z.parent || 'root') === data.parent).length;
        const result = await lediumApi.put('zones', { ...data, order: siblingCount });
        if (result) {
            handleSnackbar({ message: i18n('zone_created') as string });
        } else {
            handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
        }
        await loadData();
        notifyChanged();
    }

    async function handleMove(activeId: string, overId: string, position: TreeDndDropPosition) {
        const activeZone = zones.find((z) => z.id === activeId);
        const overZone = zones.find((z) => z.id === overId);
        if (!activeZone || !overZone) {
            return;
        }

        if (position === 'into') {
            const existingChildren = zones.filter((z) => (z.parent || 'root') === overId);
            const result = await lediumApi.patch(`zones/${activeId}`, { parent: overId, order: existingChildren.length });
            if (!result) {
                handleSnackbar({ message: i18n('zone_reorder_failed') as string, severity: 'error' });
            }
        } else {
            const newParentId = overZone.parent || 'root';
            const newParent = newParentId === 'root' ? '' : newParentId;
            const newSiblings = zones.filter((z) => (z.parent || 'root') === newParentId && z.id !== activeId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const targetIndex = newSiblings.findIndex((s) => s.id === overId);
            const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
            const reordered = [...newSiblings.slice(0, insertIndex), activeZone, ...newSiblings.slice(insertIndex)];
            const results = await Promise.all(
                reordered.map((zone, idx) =>
                    lediumApi.patch(`zones/${zone.id}`, {
                        ...(zone.id === activeId ? { parent: newParent } : {}),
                        order: idx
                    })
                )
            );
            if (results.some((r) => !r)) {
                handleSnackbar({ message: i18n('zone_reorder_failed') as string, severity: 'error' });
            }
        }

        await loadData();
        notifyChanged();
    }

    const selectedZone = React.useMemo(() => zones.find((z) => z.id === selectedId) ?? null, [zones, selectedId]);
    const existingNamesExcludingCurrent = React.useMemo(() => zones.filter((z) => z.id !== editZone?.id).map((z) => z.name), [zones, editZone]);

    const highlightedIds = React.useMemo(() => {
        if (hoveredId) {
            const sourceId = zones.find((z) => z.id === hoveredId)?.virtualSource?.zoneId;
            return sourceId ? new Set([sourceId]) : new Set<string>();
        }
        if (hoveredSourceId) {
            return new Set(zones.filter((z) => z.virtualSource?.zoneId === hoveredSourceId).map((z) => z.id));
        }
        return new Set<string>();
    }, [hoveredId, hoveredSourceId, zones]);

    const renderRowPrefix = React.useCallback(
        (zone: ZoneItem) => {
            if (zone.virtualSource) {
                const disabledReason = linkStatusByTargetId.get(zone.id);
                const sourceZone = zones.find((z) => z.id === zone.virtualSource!.zoneId);
                return <LinkedZoneTargetIconPrefix disabledReason={disabledReason} sourceZone={sourceZone} linkType={zone.virtualSource.type} />;
            }

            if (zone.linkCount) {
                const linkedVirtualZones = zones.filter((z) => z.virtualSource?.zoneId === zone.id);
                const hasError = linkedVirtualZones.some((z) => linkStatusByTargetId.get(z.id) === 'mapping_error');
                const allDisabled = !hasError && linkedVirtualZones.length > 0 && linkedVirtualZones.every((z) => !!linkStatusByTargetId.get(z.id));
                let iconColor: 'text.disabled' | 'success.main' | 'error.main' = 'success.main';
                if (hasError) {
                    iconColor = 'error.main';
                } else if (allDisabled) {
                    iconColor = 'text.disabled';
                }
                return <LinkedZonesIconPrefix zone={zone} linksRefreshKey={linksRefreshKey} iconColor={iconColor} />;
            }

            return null;
        },
        [linkStatusByTargetId, zones, linksRefreshKey]
    );

    const renderRowSuffix = React.useCallback(
        (zone: ZoneItem) => {
            if (zone.virtualSource) {
                return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>{virtualZoneStatusChip(linkStatusByTargetId.get(zone.id))}</Box>;
            }
            if (!zone.linkCount) {
                return null;
            }
            return <LinkedZonesSuffix zone={zone} linksRefreshKey={linksRefreshKey} onManageLinks={() => setManagingLinksZone(zone)} />;
        },
        [linksRefreshKey, linkStatusByTargetId]
    );

    const handleRowHover = React.useCallback(
        (id: string | null) => {
            if (!id) {
                setHoveredId(null);
                setHoveredSourceId(null);
                return;
            }
            const zone = zones.find((z) => z.id === id);
            if (zone?.virtualSource) {
                setHoveredId(id);
            } else if (zone?.linkCount) {
                setHoveredSourceId(id);
            }
        },
        [zones]
    );

    const isDeleteDisabled =
        !selectedZone || zonesWithChildren.has(selectedZone.id) || (!forceDeleteEnabled && !selectedZone.virtual && inUseZoneIds.has(selectedZone.id));

    return (
        <Container>
            <Grid container spacing={3}>
                <TitleAndDescription />
                <Grid item xs={12}>
                    <GridButtonContainer container spacing={3}>
                        <Grid item xs sx={{ textAlign: 'center' }}>
                            <RespButton variant="contained" startIcon={<AddBoxIcon />} onClick={() => setAddOpen(true)}>
                                {i18n('add_new')}
                            </RespButton>
                        </Grid>
                        <Grid item xs sx={{ textAlign: 'center' }}>
                            <RespButton
                                data-automation="zone-action-edit"
                                variant="contained"
                                startIcon={<EditIcon />}
                                disabled={!selectedZone}
                                onClick={() => {
                                    if (selectedZone) {
                                        setEditZone(selectedZone);
                                        setEditOpen(true);
                                    }
                                }}
                            >
                                {i18n('edit')}
                            </RespButton>
                        </Grid>
                        <Grid item xs sx={{ textAlign: 'center' }}>
                            <ButtonGroup
                                variant="contained"
                                color="primary"
                                disabled={!selectedZone}
                                disableElevation={!selectedZone || !!selectedZone.virtual || !zonesWithDevices.has(selectedZone.id)}
                                sx={{ margin: 1 }}
                            >
                                <Button
                                    startIcon={<CallSplitIcon />}
                                    disabled={!selectedZone || !!selectedZone.virtual || !zonesWithDevices.has(selectedZone.id)}
                                    onClick={() => selectedZone && setWizardState({ zoneId: selectedZone.id, scenario: 'copy' })}
                                >
                                    {i18n('copy')}
                                </Button>
                                <Button
                                    startIcon={<LinkIcon />}
                                    disabled={!selectedZone || !!selectedZone.virtual || !zonesWithDevices.has(selectedZone.id)}
                                    onClick={() => selectedZone && setWizardState({ zoneId: selectedZone.id, scenario: 'link-sync' })}
                                >
                                    {i18n('link')}
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs sx={{ textAlign: 'center' }}>
                            <RespButton
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                disabled={isDeleteDisabled}
                                onClick={() => selectedZone && handleDeleteClick(selectedZone)}
                            >
                                {i18n('delete')}
                            </RespButton>
                        </Grid>
                    </GridButtonContainer>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FormControlLabel
                            control={<Switch checked={forceDeleteEnabled} onChange={(e) => setForceDeleteEnabled(e.target.checked)} color="warning" />}
                            label="Allow force-delete zones"
                        />
                        <IconButton
                            size="small"
                            color="error"
                            onMouseEnter={(e) => setForceDeleteInfoAnchor(e.currentTarget)}
                            onMouseLeave={() => setForceDeleteInfoAnchor(null)}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                        <Popover
                            open={Boolean(forceDeleteInfoAnchor)}
                            anchorEl={forceDeleteInfoAnchor}
                            onClose={() => setForceDeleteInfoAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            sx={{ pointerEvents: 'none' }}
                        >
                            <Box sx={{ p: 2, maxWidth: 340 }}>
                                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 0.5 }}>
                                    Warning — use with caution
                                </Typography>
                                <Typography variant="body2">
                                    When enabled, you can delete zones along with all associated data, including linked items, projects, spectrum mixes, and
                                    lighting devices.
                                </Typography>
                            </Box>
                        </Popover>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {i18n('zone_selector_edit_info')}
                    </Alert>
                    {!loading && zones.length > 0 && (
                        <TreeDnd<ZoneItem>
                            items={zones}
                            onMove={handleMove}
                            canDropInto={(zone) => !zonesWithDevices.has(zone.id)}
                            canBeDropTarget={(zone) => !zone.virtual}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            highlightedIds={highlightedIds}
                            onRowHover={handleRowHover}
                            renderRowPrefix={renderRowPrefix}
                            renderRowSuffix={renderRowSuffix}
                        />
                    )}
                </Grid>
            </Grid>
            {editZone && (
                <AddEditZoneDialog
                    open={editOpen}
                    onClose={() => {
                        setEditOpen(false);
                        loadData();
                        if (managingLinksZone) {
                            setLinksRefreshKey((k) => k + 1);
                        }
                    }}
                    onSave={handleEditSave}
                    zones={zones}
                    zonesWithDevices={zonesWithDevices}
                    existingNames={existingNamesExcludingCurrent}
                    initialData={editZone}
                    isEdit
                    linkedZoneConfig={
                        editZone.virtual && editZone.virtualSource ? { zoneId: editZone.id, sourceZoneId: editZone.virtualSource.zoneId } : undefined
                    }
                />
            )}
            <AddEditZoneDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSave={handleAddSave}
                zones={zones}
                zonesWithDevices={zonesWithDevices}
                existingNames={zones.map((z) => z.name)}
            />
            {managingLinksZone && (
                <ManageLinksDialog
                    open={!!managingLinksZone}
                    zoneId={managingLinksZone.id}
                    title={managingLinksZone.name}
                    onClose={() => {
                        setManagingLinksZone(null);
                        setLinksRefreshKey((k) => k + 1);
                    }}
                    onEditLink={(link) => {
                        const targetZone = zones.find((z) => z.id === link.targetZoneId);
                        if (targetZone) {
                            setEditZone(targetZone);
                            setEditOpen(true);
                        }
                    }}
                    refreshKey={linksRefreshKey}
                />
            )}
            {wizardState && (
                <ZoneWizard
                    sourceZoneId={wizardState.zoneId}
                    initialScenario={wizardState.scenario}
                    onClose={() => setWizardState(null)}
                    onDone={() => {
                        setWizardState(null);
                        setLinksRefreshKey((k) => k + 1);
                        loadData();
                        notifyChanged();
                    }}
                />
            )}
        </Container>
    );
};

export default ZonesMainMenu;
